/**
 * The two columns the table adds by itself: the selection checkbox and the
 * expand chevron.
 *
 * They are column definitions rather than components, so they lived in
 * DataTableContent and cost that file its fast refresh. Nothing else about them
 * has changed.
 */
import { type Row, type Table as TanStackTable } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

export function getSelectionColumn<TData>() {
  return {
    id: 'select',
    header: ({ table }: { table: TanStackTable<TData> }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }: { row: Row<TData> }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 40,
  };
}
export function getExpandColumn<TData>() {
  return {
    id: 'expand',
    header: () => null,
    cell: ({ row }: { row: Row<TData> }) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          row.toggleExpanded();
        }}
        className="p-1 hover:bg-muted rounded"
        aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
      >
        <ChevronRight
          className={cn('h-4 w-4 transition-transform', row.getIsExpanded() && 'rotate-90')}
        />
      </button>
    ),
    enableSorting: false,
    enableHiding: false,
    size: 40,
  };
}
